/**
 * Календарь (§3.5): очередь запланированных постов по дням — вертикальная лента на красной линии —
 * и недавно опубликованные со ссылками во ВКонтакте.
 * Десктоп: дата крупно слева, линия, справа посты со временем. Телефон: одна колонка, линия слева.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { CircleCheck, Clock, ExternalLink, FolderOpen, Images, Info, RefreshCw, TriangleAlert } from "lucide-react";
import type { CalendarData, CalendarItem } from "@impuls/shared";
import { api, errText } from "../lib/api";
import { countText, dayKey, dayMonth, dayNoon, daysBetween, humanDateTime, plural, time, useDisplayTimeZone, weekday } from "../lib/format";
import { EcgLoader } from "../components/Ecg";
import { SourceTag } from "../components/PostBits";
import { PostponedModalById } from "../components/PostponedModal";
import { EmptyState, LoadError, PageHeader, useToast } from "../components/ui";

const RECENT_STEP = 8;

/** Сверка очереди с ВК: ok — сверено; mock — сверено с тестовым ВК; group — ключ сообщества сверять не умеет; error — не вышло. */
type VkCheck = "ok" | "group" | "error" | "mock";
type CalendarResp = CalendarData & { vk_check?: VkCheck };

/** Сервер без vk_check — по старинке: все будущие посты без ответа ВК значит «не получилось». */
function vkCheckOf(data: CalendarResp, now: number): VkCheck {
  if (data.vk_check) return data.vk_check;
  const upcoming = data.days.flatMap((d) => d.items).filter((i) => i.at * 1000 > now);
  return upcoming.length > 0 && upcoming.every((i) => i.in_vk === null) ? "error" : "ok";
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** Дни очереди — в поясе показа (тот же, что у времени постов), порядок сервера сохраняем. */
function groupDays(items: CalendarItem[]) {
  const days = new Map<string, CalendarItem[]>();
  for (const it of items) {
    const key = dayKey(it.at * 1000);
    const list = days.get(key);
    if (list) list.push(it);
    else days.set(key, [it]);
  }
  return [...days.entries()].map(([date, list]) => ({ date, items: list }));
}

export function CalendarPage() {
  const toast = useToast();
  useDisplayTimeZone();
  const { data, error, isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["calendar"],
    queryFn: () => api.get<CalendarResp>("/api/calendar"),
    // вышедшие посты сами переезжают в «Недавно опубликованы»
    refetchInterval: 120_000,
  });
  const [checking, setChecking] = useState(false);

  const recheck = async () => {
    setChecking(true);
    const r = await refetch();
    setChecking(false);
    if (r.error) return toast(errText(r.error), "error");
    const c = r.data ? vkCheckOf(r.data, Date.now()) : "error";
    if (c === "ok" || c === "mock") toast("Сверено с ВКонтакте");
    else if (c === "error") toast("Сверить с ВКонтакте не получилось", "error");
    // ключ сообщества: отложенные записи им не прочитать — просто обновили
  };

  if (isLoading) {
    return (
      <div className="page">
        <PageHeader title="Календарь" />
        <div className="cal-loading">
          <EcgLoader size={96} label="Собираю очередь" />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page">
        <PageHeader title="Календарь" />
        <LoadError error={error} retry={() => void refetch()} />
      </div>
    );
  }

  const now = dataUpdatedAt || 0;
  const queue = data.days.flatMap((d) => d.items);
  const days = groupDays(queue);
  const upcoming = queue.filter((i) => i.at * 1000 > now);
  const next = upcoming[0];
  const lost = upcoming.filter((i) => i.in_vk === false);
  const missing = lost.length;
  const check = vkCheckOf(data, now);
  // предупреждение — только когда сверка правда сломалась, не при ключе сообщества
  const unknown = check === "error" && upcoming.length > 0;
  const canCheck = check !== "group";

  const lead = queue.length
    ? `В очереди ${countText(queue.length, "пост", "поста", "постов")}.${next ? ` Ближайший выйдет ${humanDateTime(next.at * 1000)}.` : ""}`
    : undefined;

  return (
    <div className="page cal">
      <PageHeader
        title="Календарь"
        lead={lead}
        actions={
          queue.length > 0 && (
            <button className="btn" onClick={() => void recheck()} disabled={checking}>
              {checking ? <EcgLoader size={26} /> : <RefreshCw size={17} aria-hidden="true" />}
              {checking ? (canCheck ? "Сверяю…" : "Обновляю…") : canCheck ? "Сверить с ВКонтакте" : "Обновить"}
            </button>
          )
        }
      />

      {queue.length === 0 && data.recent.length === 0 ? (
        <QueueEmpty />
      ) : (
        <div className={`cal-layout ${data.recent.length ? "" : "cal-layout--solo"}`}>
          <section className="cal-queue" aria-label="Очередь публикаций">
            {queue.length === 0 ? (
              <QueueEmpty />
            ) : (
              <div className="sheet cal-sheet">
                {(missing > 0 || unknown) && (
                  <div className="cal-notes">
                    {missing > 0 && (
                      <div className="note-line" role="alert">
                        <TriangleAlert size={16} aria-hidden="true" />
                        {missing === 1 ? (
                          <span>
                            «{clip(lost[0].title, 60)}» не нашёлся в отложенных ВК.{" "}
                            <Link to={`/posts/${lost[0].post_id}`}>Откройте пост</Link> и запланируйте заново.
                          </span>
                        ) : (
                          <span>
                            {countText(missing, "пост", "поста", "постов")} {plural(missing, "не нашёлся", "не нашлись", "не нашлись")} в
                            отложенных ВК. Откройте каждый из отмеченных и запланируйте заново.
                          </span>
                        )}
                      </div>
                    )}
                    {unknown && (
                      <div className="note-line">
                        <Info size={16} aria-hidden="true" />
                        <span>
                          Сверить очередь с ВКонтакте не получилось. Проверьте подключение в <Link to="/settings#vk">Настройках</Link>.
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="cal-tl">
                  {days.map((d) => (
                    <Day key={d.date} date={d.date} items={d.items} now={now} />
                  ))}
                </div>
              </div>
            )}
          </section>
          {data.recent.length > 0 && <Recent items={data.recent} />}
        </div>
      )}
    </div>
  );
}

function QueueEmpty() {
  return <EmptyState title="Очередь пуста" />;
}

/* ================= День ================= */

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** «2026-10-22» → число, месяц и подпись «Завтра, четверг» / «Пятница, через 3 дня» (сегодня — в поясе показа). */
function dayInfo(key: string, now: number) {
  const today = dayKey(now || Date.now());
  const diff = daysBetween(today, key);
  const noon = dayNoon(key);
  const [num, ...rest] = dayMonth(noon).split(/\s+/);
  const month = rest.join(" ") + (key.slice(0, 4) !== today.slice(0, 4) ? ` ${key.slice(0, 4)}` : "");
  const wd = weekday(noon);
  const rel = diff === 0 ? "Сегодня" : diff === 1 ? "Завтра" : diff === -1 ? "Вчера" : null;
  const tail = rel ? wd : cap(wd);
  const away = diff > 1 ? `через ${countText(diff, "день", "дня", "дней")}` : null;
  return { num, month, rel, tail, away, diff };
}

function Day({ date, items, now }: { date: string; items: CalendarItem[]; now: number }) {
  const info = dayInfo(date, now);
  const headId = `cal-day-${date}`;
  return (
    <section className={`cal-day ${info.diff === 0 ? "is-today" : ""}`} aria-labelledby={headId}>
      <header className="cal-day__head">
        <span className="cal-day__node" aria-hidden="true" />
        <h2 id={headId} className="cal-day__date">
          <span className="cal-day__num">{info.num}</span> <span className="cal-day__month">{info.month}</span>
        </h2>
        <p className="cal-day__sub">
          {info.rel && <span className="cal-day__rel">{info.rel}, </span>}
          {info.tail}
          {info.away && <span className="cal-day__away">{info.away}</span>}
        </p>
      </header>
      <ol className="cal-day__items">
        {items.map((it) => (
          <Item key={it.post_id} item={it} now={now} />
        ))}
      </ol>
    </section>
  );
}

/* ================= Пост в очереди ================= */

type VkState = "ok" | "missing" | "due" | "unknown";

function Item({ item, now }: { item: CalendarItem; now: number }) {
  const ms = item.at * 1000;
  const state: VkState = ms <= now ? "due" : item.in_vk === false ? "missing" : item.in_vk ? "ok" : "unknown";
  const [photos, setPhotos] = useState(false);
  return (
    <li className={`cal-item cal-item--${state}`}>
      <span className="cal-item__node" aria-hidden="true" />
      <Link to={`/posts/${item.post_id}`} className="cal-item__link">
        <time className="cal-item__time" dateTime={new Date(ms).toISOString()}>
          {time(ms)}
        </time>
        <Thumb url={item.thumb_url} />
        <div className="cal-item__body">
          <h3 className="cal-item__title">{item.title || "Без названия"}</h3>
          {item.text_start && <p className="cal-item__text">{item.text_start}</p>}
        </div>
        <div className="cal-item__meta">
          {item.source === "author" ? (
            <span className="cal-item__project">
              <FolderOpen size={14} aria-hidden="true" />
              {item.project_name ?? "Без проекта"}
            </span>
          ) : (
            <SourceTag source={item.source} />
          )}
          <VkMark state={state} />
        </div>
      </Link>
      {/* пока пост не вышел: скачать фото и открыть отложенную запись во ВК */}
      {state !== "due" && item.status === "scheduled" && (
        <div className="cal-item__actions">
          <button className="btn btn--small" onClick={() => setPhotos(true)}>
            <Images size={15} aria-hidden="true" />
            {item.thumb_url ? "Фото и ссылка" : "Ссылка на пост"}
          </button>
          <PostponedModalById postId={item.post_id} open={photos} onClose={() => setPhotos(false)} />
        </div>
      )}
    </li>
  );
}

function VkMark({ state }: { state: VkState }) {
  if (state === "ok")
    return (
      <span className="cal-vk cal-vk--ok">
        <CircleCheck size={15} aria-hidden="true" />в отложенных ВК
      </span>
    );
  if (state === "missing")
    return (
      <span className="cal-vk cal-vk--warn">
        <TriangleAlert size={15} aria-hidden="true" />
        не найден в отложенных ВК — проверьте
      </span>
    );
  if (state === "due")
    return (
      <span className="cal-vk">
        <Clock size={15} aria-hidden="true" />
        выходит во ВКонтакте
      </span>
    );
  return null;
}

function Thumb({ url }: { url: string | null }) {
  if (!url)
    return (
      <span className="cal-thumb cal-thumb--empty">
        <img src="/brand/heart-red.png" alt="" />
        <span className="sr-only">Без фото</span>
      </span>
    );
  return (
    <span className="cal-thumb">
      <img src={url} alt="" loading="lazy" />
    </span>
  );
}

/* ================= Недавно опубликованы ================= */

function Recent({ items }: { items: CalendarItem[] }) {
  const [shown, setShown] = useState(RECENT_STEP);
  return (
    <aside className="panel cal-recent" aria-labelledby="cal-recent-title">
      <h2 id="cal-recent-title" className="cal-recent__title">
        Недавно опубликованы
      </h2>
      <ol className="cal-recent__list">
        {items.slice(0, shown).map((it) => (
          <li key={it.post_id} className="cal-recent__item">
            <time className="cal-recent__when" dateTime={new Date(it.at * 1000).toISOString()}>
              {cap(humanDateTime(it.at * 1000))}
            </time>
            <Link className="cal-recent__name" to={`/posts/${it.post_id}`}>
              {it.title || "Без названия"}
            </Link>
            {it.vk_url ? (
              <a className="cal-recent__vk" href={it.vk_url} target="_blank" rel="noreferrer">
                <ExternalLink size={14} aria-hidden="true" />
                Открыть во ВКонтакте
                <span className="sr-only"> (в новой вкладке)</span>
              </a>
            ) : (
              <Link className="cal-recent__vk" to={`/posts/${it.post_id}`}>
                Перейти к посту
              </Link>
            )}
          </li>
        ))}
      </ol>
      {items.length > shown && (
        <button className="btn btn--ghost btn--small cal-recent__more" onClick={() => setShown(items.length)}>
          Показать ещё {items.length - shown}
        </button>
      )}
    </aside>
  );
}
